/**
 * @file `GET/PUT /families/{familyId}/study/plans/{personId}` のクライアント。
 * @what: 学習者ごとの計画状態 (現在のステップ / 単元ごとの状態 / メモ)。
 * @why: 学習の見取り図 (カタログ) に重ねる、唯一の家族固有データ。
 * @failure: GET は plan 未作成なら 404 → `null` を返す (未設定として扱う)。他は ApiError。
 */

import { ApiError, apiFetch } from "@/shared/api/client";

export type StudyUnitStatus = "not_started" | "in_progress" | "reached";

export const STUDY_UNIT_STATUSES: readonly StudyUnitStatus[] = [
  "not_started",
  "in_progress",
  "reached",
];

export interface StudyUnitState {
  status: StudyUnitStatus;
  note?: string;
  updated_at: string;
  /** 最初に「取り組み中」になった時刻 (サーバが付ける)。 */
  started_at?: string;
  /** 「できた」になった時刻 (サーバが付ける。できた から戻すと消える)。 */
  reached_at?: string;
}

/** 除外 = 候補から外した (別の教材と重なる等)。見取り図には出さず、教材の一覧に理由のメモつきで残す。 */
export type StudyMaterialStatus = "considering" | "ready" | "in_use" | "finished" | "excluded";

export interface StudyMaterial {
  id: string;
  title: string;
  status: StudyMaterialStatus;
  unit_ids: string[];
  memo?: string;
  total_pages?: number;
  current_page?: number;
  /** どのステップで使うか (stage id)。無ければ決めていない。 */
  stage_id?: string;
  /** 領域 (model/realms.ts)。無ければ学習。 */
  realm?: string;
  /** 本なら ISBN-13 (ハイフン無し)。 */
  isbn?: string;
  publisher?: string;
  /** 商品ページ (https)。 */
  url?: string;
  updated_at: string;
}

export interface StudyMaterialInput {
  id: string;
  title: string;
  status: StudyMaterialStatus;
  unit_ids: string[];
  memo?: string;
  total_pages?: number;
  current_page?: number;
  /** どのステップで使うか (stage id)。無ければ決めていない。 */
  stage_id?: string;
  /** 領域 (model/realms.ts)。無ければ学習。 */
  realm?: string;
  /** 本なら ISBN-13 (ハイフン無し)。 */
  isbn?: string;
  publisher?: string;
  /** 商品ページ (https)。 */
  url?: string;
}

/** 残りページ数。総ページ未設定なら null。 */
export function remainingPages(m: { total_pages?: number; current_page?: number }): number | null {
  const total = m.total_pages ?? 0;
  if (total <= 0) return null;
  return Math.max(0, total - (m.current_page ?? 0));
}

/**
 * 残りが少ない (= 次の教材を考え始める目安) か。
 * 使い始めていて (current > 0)、残りが「10 ページ か 全体の 2 割」の大きい方以下。
 */
export function isRunningLow(m: { total_pages?: number; current_page?: number }): boolean {
  const total = m.total_pages ?? 0;
  const current = m.current_page ?? 0;
  const remaining = remainingPages(m);
  if (remaining === null || current <= 0) return false;
  return remaining <= Math.max(10, Math.ceil(total * 0.2));
}

/**
 * 「終わりまでにできていてほしいこと」1 項目の達成状態。
 *
 * @what: plan.exit_states の値。キーは到達度モデルの exit item id (StageExit.id) で、
 *   載っている = 達成済み。達成日 (achieved_at) はサーバが付ける (クライアントは送らない)。
 * @why: 「いつできるようになったか」を手で書かずに残す (unit の started_at と同じ線)。
 * @failure: チェックを外して保存するとキーごと消え、入れ直すと新しい達成日になる。
 */
export interface StudyExitState {
  achieved_at: string;
}

export interface StudyPlan {
  learner_person_id: string;
  schema_v: number;
  current_stage_id?: string;
  note?: string;
  unit_states: Record<string, StudyUnitState>;
  materials?: StudyMaterial[];
  prep_states?: Record<string, boolean>;
  /** うんどうチェックのお気に入りの遊び id (表示順を保つ)。家族で共有する。 */
  motor_favorites?: string[];
  /** 達成したものだけ載る (exit item id → 達成状態)。 */
  exit_states?: Record<string, StudyExitState>;
  updated_by_person_id?: string;
  created_at: string;
  updated_at: string;
}

export interface StudyUnitStateInput {
  status: StudyUnitStatus;
  note?: string;
}

export interface StudyPlanPutRequest {
  current_stage_id: string;
  note: string;
  unit_states: Record<string, StudyUnitStateInput>;
  materials: StudyMaterialInput[];
  motor_favorites?: string[];
  prep_states: Record<string, boolean>;
  /** 達成したか。達成日 (achieved_at) はサーバが付ける (false はキーごと落ちる)。 */
  exit_states: Record<string, boolean>;
}

function planPath(familyId: string, personId: string): string {
  return `/families/${encodeURIComponent(familyId)}/study/plans/${encodeURIComponent(personId)}`;
}

export async function fetchStudyPlan(
  familyId: string,
  personId: string,
  signal?: AbortSignal,
): Promise<StudyPlan | null> {
  try {
    return await apiFetch<StudyPlan>(planPath(familyId, personId), signal ? { signal } : {});
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      return null;
    }
    throw err;
  }
}

export async function putStudyPlan(
  familyId: string,
  personId: string,
  body: StudyPlanPutRequest,
): Promise<StudyPlan> {
  return apiFetch<StudyPlan>(planPath(familyId, personId), { method: "PUT", body });
}

/** 状態ボタンのクリックで回す順。 */
export function nextUnitStatus(current: StudyUnitStatus | undefined): StudyUnitStatus {
  switch (current) {
    case undefined:
    case "not_started":
      return "in_progress";
    case "in_progress":
      return "reached";
    case "reached":
      return "not_started";
  }
}

/** 画面に出す状態の呼び方。 */
export const STUDY_UNIT_STATUS_LABELS: Record<StudyUnitStatus, string> = {
  not_started: "未着手",
  in_progress: "取り組み中",
  reached: "できた",
};

/** 教材の状態ボタンのクリックで回す順。除外は回さず、押すと候補 (検討中) に戻る。 */
export function nextMaterialStatus(current: StudyMaterialStatus): StudyMaterialStatus {
  switch (current) {
    case "considering":
      return "ready";
    case "ready":
      return "in_use";
    case "in_use":
      return "finished";
    case "finished":
      return "considering";
    case "excluded":
      return "considering";
  }
}

export const STUDY_MATERIAL_STATUS_LABELS: Record<StudyMaterialStatus, string> = {
  considering: "検討中",
  ready: "用意した",
  in_use: "使っている",
  finished: "使い終えた",
  excluded: "除外",
};

/** 見取り図や「次に何をするか」に出す教材か (除外は出さない)。 */
export function isActiveMaterial(m: { status: StudyMaterialStatus }): boolean {
  return m.status !== "excluded";
}

/** 教材のクライアント採番 id (^[a-z0-9-]{1,64}$)。 */
export function newMaterialId(): string {
  return `m-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

// ---- 毎日の取り組みの記録 (progress logs) ----

/** 運動チェックの 4 段階 (◎○△×)。 */
export type StudyMotorRating = "excellent" | "stable" | "assisted" | "not_yet";

export const STUDY_MOTOR_RATINGS: readonly StudyMotorRating[] = [
  "excellent",
  "stable",
  "assisted",
  "not_yet",
];

/** 記号と読み。ボタンとマトリクスの表示に使う。 */
export const STUDY_MOTOR_RATING_MARKS: Record<StudyMotorRating, string> = {
  excellent: "◎",
  stable: "○",
  assisted: "△",
  not_yet: "×",
};

export const STUDY_MOTOR_RATING_LABELS: Record<StudyMotorRating, string> = {
  excellent: "余裕でできる",
  stable: "安定してできる",
  assisted: "たまにできる・サポートがあればできる",
  not_yet: "サポートがあってもできない",
};

/** サーバから来た値が既知の 4 段階かをランタイムでも確かめる (未知の値は表示から落とす)。 */
export function isStudyMotorRating(v: unknown): v is StudyMotorRating {
  return typeof v === "string" && (STUDY_MOTOR_RATINGS as readonly string[]).includes(v);
}

export interface StudyProgressLog {
  id: string;
  date: string;
  material_id?: string;
  /** 取り組んだ単元 (到達度モデルの unit id)。見取り図の単元から残す記録に付く。 */
  unit_id?: string;
  from_page?: number;
  to_page?: number;
  /** 領域 (model/realms.ts)。教材によらない領域の記録に付く。無ければ学習。 */
  realm?: string;
  /** 練習・活動の分数 (おんがくの練習記録が主な使い手)。 */
  minutes?: number;
  /** 運動チェックの種目 id (model/motorItems.ts)。運動の記録でなければ無し。 */
  motor_item_id?: string;
  /** 種目の出来 (◎○△×)。motor_item_id があるときだけ付く。 */
  rating?: StudyMotorRating;
  memo?: string;
  /** 機能別の機械可読 JSON (きき分けあそび epic_24 が初出)。
   *  @why: サーバは中身を解釈しない器 (valid JSON + 4096 バイトのみ検証)。語彙と parse は
   *  model/kikiwake/kikiwakeLog.ts が持ち、機能追加のたびに API を変えないための拡張点。 */
  detail?: string;
  /** 任意の添付画像 (取り組んだものの写真) の Storage パス。 */
  image_path?: string;
  /** 添付画像の暗号化方式。省略 = 平文。未知の値は「表示できない」に倒す (E2E 化の余地)。 */
  image_enc?: string;
  recorded_by_person_id?: string;
  created_at: string;
  updated_at: string;
}

export interface StudyProgressLogInput {
  id: string;
  date: string;
  material_id?: string;
  unit_id?: string;
  from_page?: number;
  to_page?: number;
  realm?: string;
  minutes?: number;
  motor_item_id?: string;
  rating?: StudyMotorRating;
  memo?: string;
  detail?: string;
  image_path?: string;
  image_enc?: string;
}

export interface StudyProgressLogPage {
  items: StudyProgressLog[];
  next_cursor?: string;
}

function progressLogsPath(familyId: string, personId: string): string {
  return `${planPath(familyId, personId)}/progress_logs`;
}

export async function fetchProgressLogs(
  familyId: string,
  personId: string,
  cursor?: string,
  signal?: AbortSignal,
  limit?: number,
): Promise<StudyProgressLogPage> {
  const params = new URLSearchParams();
  if (cursor) params.set("cursor", cursor);
  if (limit !== undefined) params.set("limit", String(limit));
  const qs = params.size > 0 ? `?${params.toString()}` : "";
  return apiFetch<StudyProgressLogPage>(
    `${progressLogsPath(familyId, personId)}${qs}`,
    signal ? { signal } : {},
  );
}

/**
 * 記録 1 件を作成する (同一 id の再送は上書き = 再試行安全)。
 * @why: opts.keepalive は きき分けあそびの途中離脱保存 (pagehide) 用。ページ破棄中も
 *   送信を続けるが body 64KB 未満の制約があるため、通常の保存では付けない。
 */
export async function postProgressLog(
  familyId: string,
  personId: string,
  body: StudyProgressLogInput,
  opts?: { keepalive?: boolean },
): Promise<StudyProgressLog> {
  return apiFetch<StudyProgressLog>(progressLogsPath(familyId, personId), {
    method: "POST",
    body,
    ...(opts?.keepalive ? { keepalive: true } : {}),
  });
}

export async function deleteProgressLog(
  familyId: string,
  personId: string,
  logId: string,
): Promise<void> {
  await apiFetch<undefined>(
    `${progressLogsPath(familyId, personId)}/${encodeURIComponent(logId)}`,
    { method: "DELETE" },
  );
}

/** 記録のクライアント採番 id。 */
export function newProgressLogId(): string {
  return `l-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
